/*
  Cart accessibility sync.

  Keeps three things in step with the real cart total:
    - the accessible name of every header cart link. The visible badge is
      aria-hidden, so the link's aria-label is the only route the count has to a
      screen reader when the link takes focus;
    - the badge's own digit and its empty-state class;
    - a polite status region, so an add, remove, or quantity change is announced
      instead of only being shown.

  Three things can change the cart, so all three feed the same sync:
    1. The theme's jQuery cart (assets/app.js.liquid -> refreshCart) calls
       window.PXUTheme.syncCartAccessibleState directly.
    2. Third-party cart apps post to /cart/*.js themselves and never reach that
       code, so cart-writing requests are watched here as well.
    3. As a backstop, the badges are observed: an app that writes the new digit
       straight into the DOM without a request we recognise still gets picked up.

  Only a total that differs from the one already on the page is announced, so a
  page load or a repeated total never interrupts a screen reader.
*/
(() => {
  const SELECTORS = {
    cartLink: '[data-td-cart-link]',
    cartCount: '[data-td-cart-count]',
    cartStatus: '[data-td-cart-status]',
  };
  const EMPTY_COUNT_CLASS = 'cart-count--0';
  const CART_WRITE_PATH = /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)/;
  const SYNC_DEBOUNCE_MS = 200;

  /*
    Module state rather than instance state, deliberately: this IIFE is loaded
    from one deferred <script> in sections/header.liquid and so runs exactly
    once per page load. There is nothing to tear down, and nothing clears
    syncTimer on unload, because this storefront does a full document load per
    navigation.

    If a Turbo/Instant-style layer is ever put in front of it, that assumption
    goes: a pending syncTimer would fire against a header that had already been
    swapped out, and lastKnownCount would carry the previous page's total. Both
    would need clearing on navigation.
  */
  let lastKnownCount = readBadgeCount();
  let syncTimer = null;

  /**
   * Reads the count currently shown in the badge. On load this is what Liquid
   * rendered; later it is whatever last wrote to the badge.
   * @returns {number} The item count shown, or 0 when there is no badge.
   */
  function readBadgeCount() {
    const badge = document.querySelector(SELECTORS.cartCount);
    const parsed = badge ? parseInt(badge.textContent.trim(), 10) : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Fills the placeholder in a translated cart string with the item count. The
   * count is substituted rather than appended so it keeps the position the
   * translator chose.
   * @param {string} template Translated string holding a %%count%% placeholder.
   * @param {number} count Cart item count.
   * @returns {string} The finished string, or '' when the string is missing.
   */
  function withCount(template, count) {
    return String(template || '').replace(/%%count%%/g, count);
  }

  /**
   * Writes a cart total into the header markup.
   * @param {number} count Number of items the cart now holds.
   * @param {boolean} announce Whether to speak the total through the status region.
   * @returns {void}
   */
  function applyCartCount(count, announce) {
    const translation = (window.PXUTheme && window.PXUTheme.translation) || {};
    const label = withCount(
      count === 1 ? translation.cart_count_one : translation.cart_count_other,
      count,
    );
    const countText = String(count);

    if (label) {
      document.querySelectorAll(SELECTORS.cartLink).forEach(link => {
        link.setAttribute('aria-label', label);
        // Where a hover tooltip exists it should say what the label says.
        if (link.hasAttribute('title')) {
          link.setAttribute('title', label);
        }
      });
    }

    document.querySelectorAll(SELECTORS.cartCount).forEach(badge => {
      // Only write when the digit is actually different. watchBadges also
      // detaches its observer around the sync, so this is the second of two
      // guards against re-entering through our own write -- worth keeping,
      // because syncCartAccessibleState is called directly by app.js.liquid
      // too, where no observer is detached.
      if (badge.textContent.trim() !== countText) {
        badge.textContent = countText;
      }
      badge.classList.toggle(EMPTY_COUNT_CLASS, count === 0);
    });

    if (!announce) {
      return;
    }

    const status = document.querySelector(SELECTORS.cartStatus);
    const message = withCount(
      count === 1 ? translation.cart_updated_one : translation.cart_updated_other,
      count,
    );

    if (status && message) {
      status.textContent = message;
    }
  }

  /**
   * Syncs the header to a cart total, announcing it only when it is a change.
   * @param {number} itemCount Number of items the cart now holds.
   * @returns {void}
   */
  function syncCartAccessibleState(itemCount) {
    const count = Number(itemCount) || 0;
    const changed = count !== lastKnownCount;

    lastKnownCount = count;
    applyCartCount(count, changed);
  }

  /**
   * Re-reads the cart from Shopify and syncs the header. Used for changes made
   * by code that does not call syncCartAccessibleState itself.
   * @returns {void}
   */
  function syncFromCartApi() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(response => (response.ok ? response.json() : null))
      .then(cart => {
        if (cart) {
          syncCartAccessibleState(cart.item_count);
        }
      })
      .catch(() => {
        /* A failed cart lookup leaves the last known total in place. */
      });
  }

  /**
   * Coalesces the burst of requests a cart app can fire into one refresh.
   * @returns {void}
   */
  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFromCartApi, SYNC_DEBOUNCE_MS);
  }

  /**
   * Reports whether a request URL writes to the cart.
   * @param {string} url Request URL, absolute or relative.
   * @returns {boolean} True when the URL is a cart write endpoint.
   */
  function isCartWrite(url) {
    return CART_WRITE_PATH.test(String(url || ''));
  }

  /**
   * Watches fetch and XMLHttpRequest for cart writes, so a change made by a
   * third-party cart app refreshes the header too.
   * @returns {void}
   */
  function watchCartRequests() {
    const nativeFetch = window.fetch;

    if (typeof nativeFetch === 'function') {
      window.fetch = (...args) => {
        const [resource] = args;
        const url = resource && resource.url ? resource.url : resource;
        const response = nativeFetch.apply(window, args);

        if (isCartWrite(url)) {
          /*
            The caller is handed `response` itself, and the chain below is a
            separate branch off it, so nothing here alters what the caller sees
            or consumes: a rejection still reaches them, and the body is left
            untouched.

            scheduleSync is called with no argument on purpose. The new total is
            re-read from /cart.js rather than parsed out of this response, both
            because the body belongs to the caller and because a cart app can
            fire several writes for one shopper action.

            The rejection handler is passed as .then's second argument rather
            than chained as .catch, so it only ever handles the fetch failing.
            A throw inside scheduleSync surfaces as an unhandled rejection
            instead of being silently swallowed.
          */
          response.then(
            () => scheduleSync(),
            () => {
              /* The request failed, so the cart did not change. */
            },
          );
        }

        return response;
      };
    }

    const nativeOpen = window.XMLHttpRequest.prototype.open;
    const nativeSend = window.XMLHttpRequest.prototype.send;

    /*
      open() records whether the URL is a cart write and send() attaches the
      listener, rather than open() doing both. An XMLHttpRequest object can be
      reused -- most code makes a fresh one per request, but some cart SDKs call
      open() again on the same instance -- and a listener attached in open()
      would still be bound when the next request on that instance loaded, firing
      a sync for a request that never touched the cart.

      Attaching per send() is correct for a reused instance too: each listener is
      { once: true }, so it is spent by the load it was attached for.
    */
    window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.tdIsCartWrite = isCartWrite(url);

      return nativeOpen.call(this, method, url, ...rest);
    };

    window.XMLHttpRequest.prototype.send = function (...args) {
      if (this.tdIsCartWrite) {
        this.addEventListener('load', scheduleSync, { once: true });
      }

      return nativeSend.apply(this, args);
    };
  }

  /**
   * Watches the badges for a digit written by code we did not intercept, and
   * syncs the rest of the header to it.
   * @returns {void}
   */
  function watchBadges() {
    const badges = Array.from(document.querySelectorAll(SELECTORS.cartCount));

    if (!badges.length) {
      return;
    }

    const OBSERVE_OPTIONS = { childList: true, characterData: true, subtree: true };

    /*
      Detached for the duration of the sync. syncCartAccessibleState writes the
      badge through applyCartCount, and that write is itself a childList
      mutation on an observed node, so leaving the observer attached means the
      callback runs a second time for our own write. applyCartCount's value
      check makes that second pass write nothing, so it was harmless, but it
      does not need to run at all.

      Dropping the records queued while detached costs nothing: each pass reads
      the badge fresh with readBadgeCount() rather than working from the
      records, so it always sees the current total.
    */
    const observer = new MutationObserver(() => {
      observer.disconnect();

      try {
        syncCartAccessibleState(readBadgeCount());
      } finally {
        badges.forEach(badge => observer.observe(badge, OBSERVE_OPTIONS));
      }
    });

    badges.forEach(badge => observer.observe(badge, OBSERVE_OPTIONS));
  }

  window.PXUTheme = window.PXUTheme || {};
  window.PXUTheme.syncCartAccessibleState = syncCartAccessibleState;

  watchCartRequests();
  watchBadges();
})();
